import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArticleProductBlock } from "@/components/journal/ArticleProductBlock";
import { ArticlePicksBlock } from "@/components/journal/ArticlePicksBlock";
import { SHOP_THIS_GUIDE_BLOCK, shopThisGuideHandles } from "../../scripts/journal-picks.mjs";
// Same JSON-LD builder and the same per-guide dates as the prerendered page.
import { articleSchemas } from "../../scripts/page-schemas.mjs";
import journalLastmod from "../../scripts/data/journal-lastmod.json";
import journalFirstCommit from "../../scripts/data/journal-first-commit.json";
import {
  JOURNAL_ARTICLES,
  JOURNAL_SLUGS,
  getArticleBySlug,
  loadArticleBody,
  type JournalArticle,
  type JournalArticleBody,
} from "@/data/journalArticles";
import NotFound from "./NotFound";
import { rememberEditorialSource } from '@/lib/editorialAttribution';

const SITE = "https://loftloom.com";

// Where the closing button goes when its label names a page other than the
// guide's first category link ("Get a Free Design Plan" used to land on a
// product listing).
const CTA_TARGETS: Record<string, string> = {
  "Get a Free Design Plan": "/design-services",
  "Read the Sofa Fabric Guide": "/ideas/boucle-vs-linen-vs-performance-fabric",
  "Shop Sectional Seating": "/category/sectionals",
  "Shop Outdoor": "/category/outdoor",
  "Shop Media Consoles": "/category/tv-stands",
  "Browse the Collections": "/categories",
};



// Replace internal /ideas/<slug> links whose target slug isn't published with
// plain text so we never ship broken links.
function pruneDeadIdeasLinks(body: string) {
  return body.replace(/\[([^\]]+)\]\(\/ideas\/([a-z0-9-]+)[^)]*\)/g, (match, label, slug) => {
    if (JOURNAL_SLUGS.has(slug)) return `[${label}](/ideas/${slug})`;
    return label;
  });
}



function stripShopThisGuide(body: string): string {
  return body.replace(SHOP_THIS_GUIDE_BLOCK, "$1").trim();
}

// Pull FAQ Q&A pairs out of an article body. The corpus uses two heading
// styles ("## FAQ" and "## Frequently asked questions") and two question
// styles ("**Question?**" and "### Question?"), so accept all of them.
function faqPairs(block: string) {
  const re =
    /(?:\*\*([^*\n]+\?)\*\*|###\s+([^\n]+\?))\s*\n+([\s\S]+?)(?=\n\*\*[^*\n]+\?\*\*|\n###\s+[^\n]+\?|\n##\s|$)/g;
  const out: { q: string; a: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(block))) {
    out.push({ q: (m[1] ?? m[2] ?? "").trim(), a: m[3].trim() });
  }
  return out;
}

function extractFAQ(body: string) {
  const m = body.match(
    /##\s+(?:FAQ|Frequently\s+Asked\s+Questions)[^\n]*\n([\s\S]+?)(?=\n##\s|$)/i,
  );
  if (m) {
    const pairs = faqPairs(m[1]);
    if (pairs.length) return pairs;
  }
  // Some guides ARE the FAQ: the questions sit as `### ...?` under topical
  // `##` headings, with no section literally named "FAQ".
  return faqPairs(body);
}

interface Section {
  heading: string | null;
  body: string;
}

/** Split markdown into the intro plus one entry per `## ` heading. */
function splitSections(body: string): Section[] {
  const lines = body.split("\n");
  const sections: Section[] = [{ heading: null, body: "" }];
  let inCode = false;
  const buffers: string[][] = [[]];
  for (const line of lines) {
    if (line.startsWith("```")) inCode = !inCode;
    if (!inCode && /^##\s+/.test(line)) {
      sections.push({ heading: line.replace(/^##\s+/, "").trim(), body: "" });
      buffers.push([]);
      continue;
    }
    buffers[buffers.length - 1].push(line);
  }
  return sections.map((s, i) => ({ ...s, body: buffers[i].join("\n").trim() }));
}

/**
 * Deterministic pull quote: the first sentence under the second `##` heading
 * that is 60–180 chars and free of markdown links, table pipes and list markers.
 * Returns null when nothing qualifies — we never rewrite or invent a sentence.
 */
function findPullQuote(sections: Section[]): string | null {
  const target = sections.filter((s) => s.heading)[1];
  if (!target) return null;
  // One paragraph at a time: joined, a list's lead-in ("…verifies:") ran into
  // the paragraph after the list, whose lines are filtered out here.
  const paragraphs = target.body
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !/^[|>#\-*\d+.]/.test(l) && !l.includes("|"));
  for (const paragraph of paragraphs) {
    for (const raw of paragraph.split(/(?<=[.!?])\s+/)) {
      // Emphasis and code marks are markdown, not text: the quote is rendered plain.
      const s = raw
        .trim()
        .replace(/\*\*|__|`/g, "")
        .replace(/(^|[^\w*])[*_]([^*_\n]+)[*_](?=[^\w*]|$)/g, "$1$2");
      if (s.length < 60 || s.length > 180) continue;
      if (/\[[^\]]*\]\(/.test(s) || s.includes("|")) continue;
      // A sentence that introduces a list ("…from the first night:") is not a quote.
      if (!/[.!?]["”’)]?$/.test(s)) continue;
      return s;
    }
  }
  return null;
}

// Wide comparison tables scroll sideways inside their own box on phones: the
// page itself is overflow-x:clip (sticky header), which used to cut off their
// last columns. -mx-4/px-4 lets the scroller reach the screen edge; tables with
// five or more columns keep a readable minimum width instead of one word per line.
const mdComponents: Components = {
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto -mx-4 px-4">
      <table {...props} className="[&:has(th:nth-child(5))]:min-w-[36rem]" />
    </div>
  ),
};

const Prose = ({ children }: { children: string }) => (
  <div
    className="prose prose-neutral max-w-none
      prose-headings:font-display prose-headings:font-semibold prose-headings:text-foreground
      prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
      prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
      prose-p:text-foreground/85 prose-p:leading-relaxed
      prose-a:text-primary prose-a:no-underline hover:prose-a:underline
      prose-strong:text-foreground
      prose-img:my-8 prose-img:w-full prose-img:rounded-none
      prose-table:text-sm prose-th:bg-muted/50 prose-th:font-semibold prose-th:text-left
      prose-td:border-border prose-th:border-border"
  >
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{children}</ReactMarkdown>
  </div>
);

const RelatedCard = ({ a }: { a: JournalArticle }) => (
  <Link
    to={`/ideas/${a.slug}`}
    className="group block"
    onMouseEnter={() => void loadArticleBody(a.slug)}
    onFocus={() => void loadArticleBody(a.slug)}
  >
    <div className="aspect-[4/3] overflow-hidden mb-3 bg-muted">
      {a.hero && (
        <img
          src={a.hero}
          alt={a.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      )}
    </div>
    <span className="text-[10px] tracking-[0.2em] uppercase text-primary font-semibold">
      {a.cluster}
    </span>
    <h3 className="font-display text-base font-semibold leading-snug mt-1 group-hover:text-primary transition-colors">
      {a.title}
    </h3>
  </Link>
);

const Article = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const [body, setBody] = useState<JournalArticleBody | null>(null);
  const [bodyFailed, setBodyFailed] = useState(false);
  const [picksRailRendered, setPicksRailRendered] = useState(false);

  useEffect(() => {
    if (!article) return;
    rememberEditorialSource('guide', article.slug);
    let cancelled = false;
    setBody(null);
    setBodyFailed(false);
    setPicksRailRendered(false);
    loadArticleBody(article.slug).then((data) => {
      if (cancelled) return;
      if (data) setBody(data);
      else setBodyFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [article?.slug]);

  const rawBody = body?.body ?? "";
  const readTime = article ? `${article.readMinutes} min read` : "";
  const faq = useMemo(() => extractFAQ(rawBody), [rawBody]);

  const sections = useMemo(
    () => (rawBody ? splitSections(pruneDeadIdeasLinks(rawBody)) : []),
    [rawBody],
  );
  const pullQuote = useMemo(() => findPullQuote(sections), [sections]);
  const pickHandles = useMemo(() => shopThisGuideHandles(rawBody), [rawBody]);

  const categoryLinks = useMemo(
    () =>
      article
        ? Array.from(
            new Set(article.internalLinks.filter((l) => l.startsWith("/category/"))),
          )
        : [],
    [article],
  );

  const related = useMemo(
    () =>
      article
        ? JOURNAL_ARTICLES.filter(
            (a) => a.slug !== article.slug && a.cluster === article.cluster,
          ).slice(0, 3)
        : [],
    [article],
  );

  // An unknown guide is a not-found page (noindex), like every other unknown
  // URL; it used to show the Journal index, indexable, under the wrong address.
  if (!article) return <NotFound />;

  const canonical = `${SITE}/ideas/${article.slug}`;
  // Article + BreadcrumbList (+ FAQPage), identical to the prerendered blocks.
  const schemas = articleSchemas(article, {
    faq,
    lastmod: (journalLastmod as Record<string, string>)[article.slug],
    firstCommit: (journalFirstCommit as Record<string, string>)[article.slug],
  });

  // Headed sections only; the intro renders before them.
  const intro = sections[0]?.heading === null ? sections[0].body : "";
  const headed = sections.filter((s) => s.heading !== null);

  // Mid images land after roughly the 2nd and 4th sections, clamped so short
  // articles still show every image they ship with.
  const midsBySection: Record<number, string[]> = {};
  (body?.mids ?? []).forEach((url, k) => {
    const slot = Math.min(k === 0 ? 1 : 1 + k * 2, Math.max(headed.length - 1, 0));
    (midsBySection[slot] ||= []).push(url);
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{article.title} | LoftLoom Journal</title>
        <meta name="description" content={article.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        {/* Same values as the prerendered head (both stay in the document). */}
        <meta property="og:title" content={`${article.title} | LoftLoom Journal`} />
        <meta property="og:description" content={article.description} />
        <meta property="og:url" content={canonical} />
        {article.hero && <meta property="og:image" content={article.hero.startsWith("http") ? article.hero : `https://loftloom.com${article.hero}`} />}
        {schemas.map((schema) => (
          <script key={schema["@type"]} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>
      <AnnouncementBar />
      <Header />
      <main className="flex-1">
        {/* Full-bleed editorial hero */}
        {article.hero ? (
          <header className="relative w-full h-[70vh] min-h-[420px] overflow-hidden bg-muted">
            <img
              src={article.hero}
              alt={article.heroAlt ?? article.title}
              className="absolute inset-0 w-full h-full object-cover"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/35 to-foreground/5" />
            <div className="absolute inset-x-0 bottom-0">
              <div className="max-w-3xl mx-auto px-4 pb-10 md:pb-14">
                <span className="text-[11px] tracking-[0.25em] uppercase text-primary-foreground/80 font-semibold">
                  {article.cluster}
                </span>
                <h1 className="font-display text-3xl md:text-5xl font-semibold text-primary-foreground leading-[1.1] mt-3">
                  {article.title}
                </h1>
                <div className="flex items-center gap-2 text-xs text-primary-foreground/75 mt-4">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{readTime}</span>
                </div>
              </div>
            </div>
          </header>
        ) : (
          <header className="max-w-3xl mx-auto px-4 pt-10">
            <span className="text-[11px] tracking-[0.25em] uppercase text-primary font-semibold">
              {article.cluster}
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-semibold text-foreground leading-[1.1] mt-3">
              {article.title}
            </h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-4">
              <Clock className="h-3.5 w-3.5" />
              <span>{readTime}</span>
            </div>
          </header>
        )}

        <div className="max-w-3xl mx-auto px-4 pt-6">
          <Breadcrumbs
            items={[{ label: "Journal", href: "/ideas" }, { label: article.title }]}
          />
        </div>

        <article className="pt-4 pb-16">
          <div className="max-w-3xl mx-auto px-4">
            <Link
              to="/ideas"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-primary mb-6"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All Articles
            </Link>
            {article.description && (
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {article.description}
              </p>
            )}
            {intro && (() => {
              const [first, ...rest] = intro.trim().split(/\n\s*\n/);
              const isAnswer = /^(for most|the (short )?answer|most |a |an |yes|no|generally|usually|aim for|choose|pick|measure)/i.test(first) && !first.startsWith("#") && !first.startsWith("-");
              if (!isAnswer) return <Prose>{intro}</Prose>;
              return (
                <>
                  <aside aria-label="Short answer" className="border-l-2 border-primary bg-secondary/40 px-5 py-4 mb-8">
                    <p className="editorial-label mb-2">Short answer</p>
                    <Prose>{first}</Prose>
                  </aside>
                  {rest.length > 0 && <Prose>{rest.join("\n\n")}</Prose>}
                </>
              );
            })()}
          </div>

          {!body && !bodyFailed && (
            <div className="max-w-3xl mx-auto px-4 animate-pulse" aria-hidden="true">
              <div className="h-4 bg-muted rounded w-full mb-3" />
              <div className="h-4 bg-muted rounded w-[92%] mb-3" />
              <div className="h-4 bg-muted rounded w-[97%] mb-3" />
              <div className="h-4 bg-muted rounded w-[70%] mb-10" />
              <div className="h-7 bg-muted rounded w-1/3 mb-5" />
              <div className="h-4 bg-muted rounded w-full mb-3" />
              <div className="h-4 bg-muted rounded w-[88%]" />
            </div>
          )}

          {bodyFailed && (
            <div className="max-w-3xl mx-auto px-4">
              <p className="text-muted-foreground">
                This article couldn&apos;t be loaded.{" "}
                <Link to="/ideas" className="text-primary underline underline-offset-4">
                  Browse all articles
                </Link>
                .
              </p>
            </div>
          )}

          {headed.map((section, i) => {
            const isFAQ = /^(?:FAQ|Frequently\s+Asked\s+Questions)/i.test(section.heading || "");
            const mids = midsBySection[i] || [];
            const hasPicks = pickHandles.length > 0;
            const categoryPath = i === 1
              ? categoryLinks[0]
              : i === 3
                ? hasPicks && picksRailRendered
                  ? categoryLinks[1] ?? categoryLinks[0]
                  : categoryLinks[1]
                : undefined;
            const sectionBody = picksRailRendered
              ? stripShopThisGuide(section.body)
              : section.body;

            return (
              <div key={section.heading}>
                <div className="max-w-3xl mx-auto px-4">
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mt-12 mb-4">
                    {section.heading}
                  </h2>
                  {isFAQ && faq.length > 0 ? (
                    <Accordion type="single" collapsible className="w-full">
                      {faq.map((f, k) => (
                        <AccordionItem key={k} value={`faq-${k}`}>
                          <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold">
                            {f.q}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="text-foreground/85 leading-relaxed">
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{f.a}</ReactMarkdown>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <Prose>{sectionBody}</Prose>
                  )}
                </div>

                {i === 1 && pullQuote && (
                  <div className="max-w-4xl mx-auto px-4 my-12 md:my-16">
                    <blockquote className="font-display text-xl md:text-3xl text-foreground leading-relaxed text-center border-y border-border/60 py-8 md:py-10">
                      {pullQuote}
                    </blockquote>
                  </div>
                )}

                {mids.map((mid) => (
                  <figure key={mid} className="max-w-5xl mx-auto px-4 my-10 md:my-14">
                    <img
                      src={mid}
                      alt={article.title}
                      loading="lazy"
                      className="w-full h-auto object-cover"
                    />
                  </figure>
                ))}

                {i === 1 && hasPicks && (
                  <div className="max-w-3xl mx-auto px-4">
                    <ArticlePicksBlock
                      handles={pickHandles}
                      categoryPath={categoryLinks[0]}
                      onResolvedChange={setPicksRailRendered}
                    />
                  </div>
                )}

                {categoryPath && !(i === 1 && hasPicks) && (
                  <div className="max-w-3xl mx-auto px-4">
                    <ArticleProductBlock
                      categoryPath={categoryPath}
                      heading={i === 1 ? "Shop the category" : "More to explore"}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {article.cta && (
            <div className="max-w-3xl mx-auto px-4">
              <div className="mt-12 border border-border/60 bg-card p-6 md:p-8 text-center">
                <p className="text-sm text-muted-foreground mb-3 tracking-wide uppercase">
                  Bring it home
                </p>
                <Button
                  asChild
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none text-[12px] tracking-widest uppercase px-8 py-5"
                >
                  <Link to={CTA_TARGETS[article.cta] ?? categoryLinks[0] ?? "/categories"}>
                    {article.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </article>

        {related.length > 0 && (
          <section className="border-t border-border/60 bg-muted/20">
            <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-8">
                More from {article.cluster}
              </h2>
              <div className="grid md:grid-cols-3 gap-x-6 gap-y-10">
                {related.map((a) => (
                  <RelatedCard key={a.slug} a={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Article;
